/**
 * Holistic Beauty Essentials - Custom JavaScript
 * Enhanced functionality for the holistic beauty theme
 */

class HolisticBeautyTheme {
  constructor() {
    this.init();
  }

  init() {
    this.setupMobileMenu();
    this.setupTestimonialCarousel();
    this.setupNewsletterForm();
    this.setupProductCards();
    this.setupSmoothScrolling();
    this.setupLazyLoading();
    this.setupContactForms();
  }

  // Mobile Menu Toggle
  setupMobileMenu() {
    const mobileMenuBtn = document.querySelector('.header-holistic__mobile-menu');
    const mobileNav = document.getElementById('mobile-nav');
    
    if (mobileMenuBtn && mobileNav) {
      mobileMenuBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        mobileNav.classList.toggle('active');
        document.body.classList.toggle('menu-open');
      });

      // Close menu when clicking outside
      document.addEventListener('click', (e) => {
        if (!mobileMenuBtn.contains(e.target) && !mobileNav.contains(e.target)) {
          mobileNav.classList.remove('active');
          document.body.classList.remove('menu-open');
        }
      });

      // Close menu on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          mobileNav.classList.remove('active');
          document.body.classList.remove('menu-open');
        }
      });
    }
  }

  // Testimonial Carousel
  setupTestimonialCarousel() {
    const track = document.querySelector('.testimonials__track');
    const slides = document.querySelectorAll('.testimonials__slide');
    const prevBtn = document.querySelector('.testimonials__nav--prev');
    const nextBtn = document.querySelector('.testimonials__nav--next');
    
    if (!track || !slides.length) return;

    let currentSlide = 0;
    const totalSlides = slides.length;
    let autoplayInterval;

    const updateCarousel = () => {
      const translateX = -currentSlide * 100;
      track.style.transform = `translateX(${translateX}%)`;
    };

    const nextSlide = () => {
      currentSlide = (currentSlide + 1) % totalSlides;
      updateCarousel();
    };

    const prevSlide = () => {
      currentSlide = (currentSlide - 1 + totalSlides) % totalSlides;
      updateCarousel();
    };

    const startAutoplay = () => {
      autoplayInterval = setInterval(nextSlide, 5000);
    };

    const stopAutoplay = () => {
      clearInterval(autoplayInterval);
    };

    // Event listeners
    if (nextBtn) nextBtn.addEventListener('click', nextSlide);
    if (prevBtn) prevBtn.addEventListener('click', prevSlide);

    // Pause autoplay on hover
    track.addEventListener('mouseenter', stopAutoplay);
    track.addEventListener('mouseleave', startAutoplay);

    // Start autoplay
    startAutoplay();
  }

  // Newsletter Form
  setupNewsletterForm() {
    const newsletterForm = document.querySelector('.footer-holistic__newsletter-form');
    
    if (newsletterForm) {
      newsletterForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const email = e.target.querySelector('input[type="email"]').value;
        const button = e.target.querySelector('button[type="submit"]');
        
        if (!email) return;

        // Show loading state
        const originalText = button.textContent;
        button.textContent = 'Signing up...';
        button.disabled = true;

        try {
          // Simulate API call (replace with actual newsletter signup)
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Show success message
          button.textContent = 'Thank you!';
          button.style.backgroundColor = '#4CAF50';
          
          // Reset form
          setTimeout(() => {
            e.target.reset();
            button.textContent = originalText;
            button.disabled = false;
            button.style.backgroundColor = '';
          }, 2000);
          
        } catch (error) {
          button.textContent = 'Try again';
          button.disabled = false;
        }
      });
    }
  }

  // Product Cards Enhancement
  setupProductCards() {
    const productCards = document.querySelectorAll('.product-card');
    
    productCards.forEach(card => {
      const button = card.querySelector('.product-card__button');
      
      if (button) {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          
          // Add to cart animation
          button.textContent = 'Adding...';
          button.disabled = true;
          
          setTimeout(() => {
            button.textContent = 'Added!';
            button.style.backgroundColor = '#4CAF50';
            
            setTimeout(() => {
              button.textContent = 'SHOP NOW';
              button.disabled = false;
              button.style.backgroundColor = '';
            }, 1500);
          }, 1000);
        });
      }
    });
  }

  // Smooth Scrolling
  setupSmoothScrolling() {
    const links = document.querySelectorAll('a[href^="#"]');
    
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (href === '#') return;
        
        e.preventDefault();
        const target = document.querySelector(href);
        
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  // Lazy Loading for Images
  setupLazyLoading() {
    const images = document.querySelectorAll('img[data-src]');
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.classList.remove('lazy');
            observer.unobserve(img);
          }
        });
      });

      images.forEach(img => imageObserver.observe(img));
    } else {
      // Fallback for older browsers
      images.forEach(img => {
        img.src = img.dataset.src;
        img.classList.remove('lazy');
      });
    }
  }

  // Contact and Affiliate Forms Email Handling
  setupContactForms() {
    const affiliateForm = document.getElementById('AffiliateForm');
    const contactForm = document.getElementById('ContactForm');

    // Handle affiliate form submission
    if (affiliateForm) {
      affiliateForm.addEventListener('submit', (e) => {
        // Wait for Shopify form to process, then send email
        setTimeout(() => {
          if (affiliateForm.querySelector('.affiliate-holistic__form-success')) {
            this.sendAffiliateEmail(affiliateForm);
          }
        }, 500);
      });
    }

    // Handle contact form submission
    if (contactForm) {
      contactForm.addEventListener('submit', (e) => {
        // Wait for Shopify form to process, then send email
        setTimeout(() => {
          if (contactForm.querySelector('.contact-us__form-success')) {
            this.sendContactEmail(contactForm);
          }
        }, 500);
      });
    }
  }

  // Send affiliate form email
  sendAffiliateEmail(form) {
    const formData = new FormData(form);
    const firstName = formData.get('contact[first_name]') || '';
    const lastName = formData.get('contact[last_name]') || '';
    const email = formData.get('contact[email]') || '';
    const phone = formData.get('contact[phone]') || '';
    const website = formData.get('contact[website]') || '';
    const message = formData.get('contact[body]') || '';

    const emailSubject = 'New Affiliate Application - Holistic Beauty';
    const emailBody = `
New Affiliate Application Received

Name: ${firstName} ${lastName}
Email: ${email}
Phone: ${phone || 'Not provided'}
Website/Social Media: ${website || 'Not provided'}

Message:
${message}

---
This email was sent from the affiliate application form on your website.
Reply to: ${email}
    `.trim();

    this.sendEmailToHolisticBeauty(emailSubject, emailBody, email, `${firstName} ${lastName}`);
  }

  // Send contact form email
  sendContactEmail(form) {
    const formData = new FormData(form);
    const name = formData.get('contact[name]') || '';
    const email = formData.get('contact[email]') || '';
    const phone = formData.get('contact[phone]') || '';
    const subject = formData.get('contact[subject]') || 'Contact Form Submission';
    const message = formData.get('contact[body]') || '';

    const emailSubject = `Contact Form: ${subject}`;
    const emailBody = `
New Contact Form Submission

Name: ${name}
Email: ${email}
Phone: ${phone || 'Not provided'}
Subject: ${subject}

Message:
${message}

---
This email was sent from the contact form on your website.
Reply to: ${email}
    `.trim();

    this.sendEmailToHolisticBeauty(emailSubject, emailBody, email, name);
  }

  // Send email to holisticbeauty
  sendEmailToHolisticBeauty(subject, body, replyTo, replyToName) {
    // Try EmailJS first if configured
    if (typeof emailjs !== 'undefined' && window.emailjsConfig) {
      emailjs.send(
        window.emailjsConfig.serviceId,
        window.emailjsConfig.templateId,
        {
          to_email: window.emailjsConfig.toEmail || 'holisticbeauty@example.com',
          from_name: replyToName || 'Website Visitor',
          from_email: replyTo || 'noreply@example.com',
          subject: subject,
          message: body,
          reply_to: replyTo || 'noreply@example.com'
        }
      ).then(
        (response) => {
          console.log('Email sent successfully via EmailJS', response.status, response.text);
        },
        (error) => {
          console.error('EmailJS error:', error);
          // Fallback: log for manual sending
          this.logEmailForManualSending(subject, body, replyTo);
        }
      );
    } else {
      // Log email details for manual sending or server-side processing
      this.logEmailForManualSending(subject, body, replyTo);
    }
  }

  // Log email details (can be sent to server endpoint if available)
  logEmailForManualSending(subject, body, replyTo) {
    console.log('Email to send to holisticbeauty:');
    console.log('Subject:', subject);
    console.log('Body:', body);
    console.log('Reply To:', replyTo);
    
    // If you have a server endpoint, you can send it here
    // Example:
    // fetch('/api/send-email', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ subject, body, replyTo, to: 'holisticbeauty@example.com' })
    // });
  }

  // Utility Methods
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new HolisticBeautyTheme();
});

// Export for potential external use
window.HolisticBeautyTheme = HolisticBeautyTheme;
